import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  shouldShowConsentBanner, 
  saveConsentPreferences,
  type CookieConsentType 
} from '@/lib/cookieConsent';

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check after a short delay to avoid flash
    const timer = setTimeout(() => {
      if (shouldShowConsentBanner()) {
        setIsVisible(true);
        // Trigger animation
        setTimeout(() => setIsAnimating(true), 50);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleConsent = (type: CookieConsentType) => {
    saveConsentPreferences(type);
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-4 right-4 z-[9999] max-w-sm transition-all duration-300 ease-out ${
        isAnimating 
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-4 scale-95'
      }`}
    >
      {/* Main banner card */}
      <div className="bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 pb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Cookie className="w-4 h-4 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground">
              {t('cookies.banner.title', 'Cookie Settings')}
            </span>
          </div>
          <button 
            onClick={() => handleConsent('declined')}
            className="p-1 rounded-md hover:bg-muted/50 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t('cookies.banner.description', 'We use cookies to enhance your experience, analyze traffic, and personalize content. Your data helps us improve our services.')}
          </p>
        </div>

        {/* Expandable details */}
        <div className="px-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors mb-2"
          >
            <Shield className="w-3 h-3" />
            {t('cookies.banner.learnMore', 'Learn more')}
            {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showDetails && (
            <div className="text-xs text-muted-foreground space-y-2 pb-3 border-t border-border/30 pt-3 animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                <span><strong className="text-foreground/80">{t('cookies.banner.essential', 'Essential')}:</strong> {t('cookies.banner.essentialDesc', 'Required for site functionality')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                <span><strong className="text-foreground/80">{t('cookies.banner.analytics', 'Analytics')}:</strong> {t('cookies.banner.analyticsDesc', 'Help us improve our site')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                <span><strong className="text-foreground/80">{t('cookies.banner.marketing', 'Marketing')}:</strong> {t('cookies.banner.marketingDesc', 'Personalized recommendations')}</span>
              </div>
              <Link 
                to="/cookies" 
                className="inline-block text-primary hover:underline mt-1"
              >
                {t('cookies.banner.fullPolicy', 'View full Cookie Policy →')}
              </Link>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 pt-2 border-t border-border/20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleConsent('essential')}
            className="flex-1 text-xs h-8 hover:bg-muted/50"
          >
            {t('cookies.banner.essentialOnly', 'Essential Only')}
          </Button>
          <Button
            size="sm"
            onClick={() => handleConsent('all')}
            className="flex-1 text-xs h-8 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {t('cookies.banner.acceptAll', 'Accept All')}
          </Button>
        </div>
      </div>
    </div>
  );
}
